/**
 * Wipe-and-restart confirmation (portal-spec §4.12). Reached via the emailed signed link
 * (locker.heivol.com/wipe?token=…).
 *
 * CRITICAL (INV-16): this page performs NO mutation on load. Email link-scanners (SafeLinks /
 * Proofpoint / Mimecast / Gmail fetch) and browser prefetch issue GETs against links, and a GET
 * must never wipe a vault. Destruction happens ONLY when the signed-in user types WIPE and submits
 * (a POST). No unlock / UMK is required — that is the whole point of wipe: the user has lost both
 * the passphrase and the recovery code, so they cannot produce a umk-proof. The token + a valid
 * heivol-id session + the typed word are the authorization.
 */
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { useSession } from '../store/session';
import { Button, Card, ErrorNote, Field, Input } from '../components/ui';
import { readWipeToken, stripWipeToken } from '../lib/wipe-token';

export function WipeConfirm() {
  const wipeConfirm = useSession((s) => s.wipeConfirm);
  const wipeCancel = useSession((s) => s.wipeCancel);
  // Read once (pure), then strip `?token=` from the address bar / history in an effect so the
  // bearer token does not linger or leak via Referer. The value stays in component state.
  const [token] = useState<string | null>(() => readWipeToken(window.location.search));
  useEffect(() => {
    stripWipeToken(window.location, window.history);
  }, []);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);

  const cancel = (): void => {
    setCancelBusy(true);
    setError(null);
    void wipeCancel()
      .then(() => window.location.replace('/'))
      .catch(() => {
        setError('Could not cancel the wipe. Sign in and try again.');
        setCancelBusy(false);
      });
  };

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (token === null || confirmText !== 'WIPE') {
      return;
    }
    setBusy(true);
    setError(null);
    void wipeConfirm(token)
      .then(() => {
        setDone(true);
        // Land the user in the fresh-start enrolment: a full reload clears any in-memory state and
        // re-runs init() → me() (now un-enrolled) → the /welcome wizard.
        window.location.replace('/');
      })
      .catch(() => {
        setError(
          'This wipe link is invalid, already used, or expired. Start over from your vault.',
        );
        setBusy(false);
      });
  };

  return (
    <div className="grid min-h-full place-items-center p-4">
      <Card className="w-full max-w-md space-y-5">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-error">Wipe your vault?</h1>
          <p className="text-sm text-text-secondary">
            This permanently destroys <strong>every item</strong> in your vault. It cannot be
            undone. Your heivol-id login stays — you&rsquo;ll set up a brand-new, empty vault right
            after.
          </p>
        </div>

        {token === null ? (
          <ErrorNote>
            This link is missing its token. Open the wipe link from your email again.
          </ErrorNote>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Type WIPE to confirm" hint="Case-sensitive.">
              <Input
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoFocus
              />
            </Field>

            {error !== null && <ErrorNote>{error}</ErrorNote>}

            <Button
              type="submit"
              disabled={busy || done || cancelBusy || confirmText !== 'WIPE'}
              className="w-full"
            >
              {busy ? 'Wiping your vault…' : 'Permanently wipe and start over'}
            </Button>

            <button
              type="button"
              onClick={cancel}
              disabled={busy || done || cancelBusy}
              className="block w-full text-center text-xs text-text-secondary underline underline-offset-2 hover:text-text disabled:opacity-50"
            >
              {cancelBusy ? 'Cancelling…' : 'Cancel — keep my vault'}
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
